/**
 * Created by Ning Jiang on 7/14/2016.
 */
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {TextStyleOptions} from "@nolimitcity/slot-game/bin/text/TextStyleOptions";
import {TweenLite, Linear, TimelineLite, Power3} from "gsap";
import {MathHelper} from "../../core/utils/MathHelper";
import {Oktoberfest} from "../Oktoberfest";
import {EasePack} from "gsap/dist/EasePack";
import EaseFunction = gsap.EaseFunction;
import {OktoberfestSound} from "../sound/OktoberfestSound";
import {Layers, OktoberfestLayers} from "./OktoberfestLayers";
import {OktoberfestGameConfig} from "../OktoberfestGameConfig";
import {Helper} from "@nolimitcity/slot-launcher/bin/utils/Helper";

export interface ITextStyle {
    topText: string,
    bottomText: string,
    topPos: Ipos,
    bottomPos: Ipos,
}

export interface Ipos {
    x: number,
    y: number
}

export class OktoberfestPartyView extends PIXI.Container {

    private static PARTICLES_IMAGE_NAME: string = "partyIntroParticles";
    public isSkipped: boolean = false;
    private _partyContainer: PIXI.Container;
    private _background: PIXI.Container;
    private _banner: PIXI.Container;
    private _particles: PIXI.Sprite;
    private _isPlaying: boolean;
    private _textContainer: PIXI.Container;
    private _stageWidth: number;
    private _stageHeight: number;
    private _partySymbol: string;
    private _backgroundAlpha: number = 0.4;
    private _flyingTextStyle: TextStyleOptions;
    private _bannerWidth: number = NolimitApplication.screenBounds.width;
    private _bannerPlateColors: { [symId: string]: number } =
        {
            W: 0x6C5F16,
            M1: 0x3D235A,
            M2: 0x532C1A,
            M3: 0x45531A,
            M4: 0x284A24,
            M5: 0x243E4A
        };

    private _partyIntroSounds: { [symId: string]: string } = {
        W: OktoberfestSound.SOUND_NAME_VOICE_PRETZEL_PARTY,
        M1: OktoberfestSound.SOUND_NAME_VOICE_HYPNOTIC_HORNS,
        M2: OktoberfestSound.SOUND_NAME_VOICE_KOOL_KEGS,
        M3: OktoberfestSound.SOUND_NAME_VOICE_AWESOME_ACCORDIANS,
        M4: OktoberfestSound.SOUND_NAME_VOICE_HAPPY_HATS,
        M5: OktoberfestSound.SOUND_NAME_VOICE_SUPER_SAUSAGES
    };

    private _textsSettings: { [symId: string]: ITextStyle } = {
        W: {
            topText: "Pretzel",
            bottomText: "Party",
            topPos: {x: -33, y: 0},
            bottomPos: {x: 67, y: 0},
        },
        M1: {
            topText: "Hypnotic",
            bottomText: "Horns",
            topPos: {x: -25, y: 0},
            bottomPos: {x: -10, y: 0},
        },
        M2: {
            topText: "Kool",
            bottomText: "Kegs",
            topPos: {x: -72, y: 0},
            bottomPos: {x: 70, y: 0},
        },
        M3: {
            topText: "Awesome",
            bottomText: "Accordions",
            topPos: {x: -98, y: 0},
            bottomPos: {x: 65, y: 0},
        },
        M4: {
            topText: "Happy",
            bottomText: "Hats",
            topPos: {x: -31, y: 0},
            bottomPos: {x: 70, y: 0},
        },
        M5: {
            topText: "Super",
            bottomText: "Sausages",
            topPos: {x: -118, y: 0},
            bottomPos: {x: 48, y: 0},
        }
    };

    private _bannerPlate: PIXI.Graphics;
    private _bannerHeight = 75;
    private _topContainer: PIXI.Container;
    private _bottomContainer: PIXI.Container;
    private _texts: { [symId: string]: any };
    private _partyTimeLine: TimelineLite;
    private _quickStopTimescale: number = 3;
    private _defaultTimeScale: number = 1;
    private _previousTopContainerX: number;

    private _activePartyIntroSoundName: string;
    private _hasPlayed: boolean = false;

    constructor() {
        super();
        this._isPlaying = false;
        this.initAnimations();
        this.onLandscape();
        OktoberfestLayers.getLayer(Layers.PARTY_WIN)!.addChild(this);
    }

    public onPartyStart(data: { partySymbol: string }) {
        this.isSkipped = false;
        this._partySymbol = data.partySymbol;
        this._isPlaying = true;
        this._hasPlayed = true;
        this.addChild(this._partyContainer);
        this._partyTimeLine.timeScale(this._defaultTimeScale);
        this.playPartyTimeLine();
    }

    public onAllReelsStopped(): void {
        if (this._hasPlayed) {
            this._hasPlayed = false;
            this.stopPartyDrumSound();
            this.fadeAmbience(1, 600);
        }
    }

    public onQuickStopStoppingSpin(): void {
        if (!this._isPlaying) {
            return;
        }
        this._partyTimeLine.timeScale(this._quickStopTimescale);
    }

    public fadeAmbience(volume: number, delay: number): void {
        OktoberfestSound.fadeMusic(volume, delay);
    }

    public onOrientationChanged(): void {
        this._bannerWidth = NolimitApplication.screenBounds.width;
        // #673
        this.setBannerPlateColor(this._bannerPlateColors[this._partySymbol]);
          if (this._background.children.length > 0) {
            let graphics = this._background.getChildAt(0) as PIXI.Graphics;
            graphics.clear();
            graphics.beginFill(0x000000, 1);
            graphics.drawRect(0, -4,
                NolimitApplication.screenBounds.width,
                NolimitApplication.screenBounds.height);
            graphics.endFill();
            this._background.pivot = new PIXI.Point(graphics.width * 0.5, graphics.height * 0.5);
        }
    }

    protected onLandscape(): void {
        const scale: number = NolimitApplication.globalScale;
        this._banner.width = this._bannerWidth;
        this._banner.position.y = -40;
        this._textContainer.scale.set(scale, scale);
        this._background.scale.set(1);
    }

    private initAnimations() {
        this._partyContainer = new PIXI.Container();
        this.addChild(this._partyContainer);
        this._stageWidth = NolimitApplication.screenBounds.width;
        this._stageHeight = NolimitApplication.screenBounds.height;
        this._flyingTextStyle = {
            fontFamily: "Atreyu",
            fontSize: "100px",
            fill: "#C3BE5A",
            stroke: "#1B1A15",
            strokeThickness: 2,
            dropShadow: true,
            dropShadowColor: "#1B1A15",
            dropShadowAngle: 0.872665, //PhotoShop 120 real 50
            dropShadowDistance: 5,
            padding: 20
        };
        this._textContainer = new PIXI.Container();
        this._background = this.createBackground();
        this._partyContainer.addChild(this._background);
        this._partyContainer.position.set(360);
        this._banner = this.createBanner();
        this._partyContainer.addChild(this._banner);
        this.createParticles();
        this.createAllTexts();
        this.createTimeLineAnimation();
        this._partyContainer.scale.set(1);
        this._banner.scale.set(1, 0);
    }

    private createBanner(): PIXI.Container {
        let banner: PIXI.Container = new PIXI.Container();
        this._bannerPlate = new PIXI.Graphics();
        this._bannerPlate.beginFill(0x124467, 1);
        this._bannerPlate.drawRect(0, 0, this._bannerWidth, this._bannerHeight);
        this._bannerPlate.endFill();
        banner.addChild(this._bannerPlate);
        banner.name = "banner";
        this._bannerPlate.name = "bannerPlate";
        banner.pivot = new PIXI.Point(this._bannerPlate.width * 0.5, this._bannerPlate.height * 0.5);
        banner.scale.y = 0;
        banner.scale.x = 1;

        let lineTL: PIXI.Sprite = new PIXI.Sprite(Oktoberfest.assetLoader.getTextures("partyModeGoldBorder")[0]);
        lineTL.anchor.set(0, 1);
        lineTL.position.set(0, 0);
        lineTL.name = "lineTL";
        banner.addChild(lineTL);
        let lineBL: PIXI.Sprite = new PIXI.Sprite(Oktoberfest.assetLoader.getTextures("partyModeGoldBorder")[0]);
        lineBL.anchor.set(0, 0);
        lineBL.position.set(0, this._bannerPlate.height);
        banner.addChild(lineBL);
        lineBL.name = "lineBL";
        lineBL.width = lineTL.width = this._bannerWidth;
        return banner;
    }

    private createBackground(): PIXI.Container {
        const graphics: PIXI.Graphics = new PIXI.Graphics();
        graphics.beginFill(0x000000, 1);
        graphics.drawRect(0, -4, NolimitApplication.screenBounds.width, NolimitApplication.screenBounds.height);
        graphics.endFill();
        const background: PIXI.Container = new PIXI.Container;
        background.alpha = 0;
        background.addChild(graphics);
        background.pivot = new PIXI.Point(graphics.width * 0.5, graphics.height * 0.5);
        return background;
    }

    private createAllTexts(): void {
        // Change this offset values will break the meet position.
        this._topContainer = new PIXI.Container();
        this._bottomContainer = new PIXI.Container();
        this._texts = {
            W: null,
            M1: null,
            M2: null,
            M3: null,
            M4: null,
            M5: null
        };

        for (let symbolId in this._texts) {
            let setting = this._textsSettings[symbolId];
            let topTextObject: PIXI.Text = new PIXI.Text(setting.topText, this._flyingTextStyle);
            topTextObject.position.set(0, setting.topPos.y);
            topTextObject.anchor.set(0.5, 0.5);
            topTextObject.visible = false;
            this._topContainer.width = 200;
            this._topContainer.addChild(topTextObject);
            this._topContainer.position.set(0, -100);

            let bottomTextObject: PIXI.Text = new PIXI.Text(setting.bottomText, this._flyingTextStyle);
            bottomTextObject.anchor.set(0.5, 0.5);
            bottomTextObject.position.set(0, setting.bottomPos.y);
            bottomTextObject.visible = false;
            this._bottomContainer.width = 200;
            this._bottomContainer.addChild(bottomTextObject);
            this._bottomContainer.position.set(0, -20);
            this._texts[symbolId] = {topText: topTextObject, bottomText: bottomTextObject};
        }
        this._textContainer.name = "textContainer";
        this._topContainer.name = "topContainer";
        this._bottomContainer.name = "bottomContainer";
        this._textContainer.addChild(this._topContainer);
        this._textContainer.addChild(this._bottomContainer);
        this._partyContainer.addChild(this._textContainer);
    }

    private createTimeLineAnimation(): void {
        let defaultEase: EaseFunction = Power3.easeIn;
        let slowMoEase: EaseFunction = EasePack.SlowMo.config(0.9, 0.98);
        this._textContainer.alpha = 0;
        this._partyTimeLine = new TimelineLite({paused: true, onComplete: this.onPartyTimeLineComplete.bind(this)});
        //background in
        this._partyTimeLine.add([
            () => Oktoberfest.dimOverLay.onDimStage(),
            () => this.fadeAmbience(0, 600),
            () => this.playBannerInSound(),
            new TweenLite(this._background, 0.6, {alpha: this._backgroundAlpha, ease: Power3.easeIn})]
        );
        this._partyTimeLine.add([], 0.5);
        //banner in
        this._partyTimeLine.add([], "+=0.1");

        this._partyTimeLine.add(() => {
            this._textContainer.alpha = 1;
        }, 0.95);
        //Flying texts + particles
        this._partyTimeLine.add([
            () => this.playPartyIntroSound(),
            () => this.onTextAnimationStart(),
            new TweenLite(this._banner.scale, 0.13, {y: 1, ease: defaultEase}),
            new TweenLite(this._particles, 0.5, {alpha: 1}),
            new TweenLite(this._particles, 2.0, {x: 0, ease: Linear.easeNone}),

            new TweenLite(this._topContainer, 2.0, {
                x: ( this._stageWidth * 0.5 ) + 340,
                ease: slowMoEase,
                //onStart: () => this.onTextAnimationStart(),
                onUpdate: () => this.onTextAnimationUpdate(),
                onComplete: () => this.onTextAnimationComplete()
            }),
            new TweenLite(this._bottomContainer, 2.0, {x: -( this._stageWidth * 0.5 ) - 340, ease: slowMoEase})
        ]);


        //banner out + particles out
        this._partyTimeLine.add([
            () => {
                if (!this.isSkipped) {
                    this.playPartyDrumSound()
                }
            },
            new TweenLite(this._particles, 0.13, {alpha: 0}),
            new TweenLite(this._banner.scale, 0.13, {y: 0, ease: defaultEase})
        ]);

        //background out
        this._partyTimeLine.add([
            new TweenLite(this._background, 1, {alpha: 0, ease: defaultEase}),
        ]);
    }

    private onTextAnimationStart(): void {
        this._topContainer.x = -( this._stageWidth * 0.5 ) - 340;
        this._bottomContainer.x = ( this._stageWidth * 0.5 ) + 340;
        this._previousTopContainerX = this._topContainer.x;
        this._texts[this._partySymbol].topText.visible = true;
        this._texts[this._partySymbol].bottomText.visible = true;
    }

    private onTextAnimationComplete(): void {
        this._texts[this._partySymbol].topText.visible = false;
        this._texts[this._partySymbol].bottomText.visible = false;
    }

    private onTextAnimationUpdate(): void {
        let vel = Math.floor(this._topContainer.x - this._previousTopContainerX);
        let scale = MathHelper.map(vel, 0, 600 * this._partyTimeLine.timeScale(), 1, 0.05);

        this._topContainer.scale.y = scale;
        this._bottomContainer.scale.y = scale;

        this._previousTopContainerX = this._topContainer.x;
    }

    private createParticles(): void {
        this._particles = new PIXI.Sprite(Oktoberfest.assetLoader.getTextures(OktoberfestPartyView.PARTICLES_IMAGE_NAME)[0]);
        this._particles.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._particles.anchor.set(0.5, 0.5);
        this._particles.position.set(-50, 0);
        this._particles.alpha = 0;
        this._textContainer.addChild(this._particles);
    }

    private playPartyTimeLine(): void {
        this._banner.scale.y = 0;
        this._banner.scale.x = 1;
        this.onTextAnimationStart();
        //Set color of banner
        this.setBannerPlateColor(this._bannerPlateColors[this._partySymbol]);

        //Sound to play
        this._activePartyIntroSoundName = this._partyIntroSounds[this._partySymbol];


        this._partyTimeLine.play();
    }

    private setBannerPlateColor(color: number): void {
        this._bannerWidth = NolimitApplication.screenBounds.width;
        this._bannerWidth = OktoberfestGameConfig.GAME_WIDTH<this._bannerWidth?this._bannerWidth:OktoberfestGameConfig.GAME_WIDTH;
        this._bannerPlate.clear();
        this._bannerPlate.beginFill(color, 1);
        this._bannerPlate.drawRect(0, 0, this._bannerWidth, this._bannerHeight);
        this._bannerPlate.endFill();
        this._banner.children.forEach((item: any) => {
            if (item instanceof PIXI.Sprite) {
                ( item as PIXI.Sprite ).width = this._bannerWidth;
            }
        });
        this._banner.pivot = new PIXI.Point(this._bannerPlate.width * 0.5, this._bannerPlate.height * 0.5);
        this._bannerPlate.scale.set(1);
    }

    private onPartyTimeLineComplete(): void {
        this._partyTimeLine.timeScale(this._defaultTimeScale);
        this._partyTimeLine.pause(0);
        this._textContainer.alpha = 0;
        this.onAllAnimationComplete();
    }

    private onAllAnimationComplete(): void {
        this._isPlaying = false;
        this.removeChild(this._partyContainer);
    }

    private playBannerInSound(): void {
        OktoberfestSound.soundPlayer.playTrack({name: OktoberfestSound.SOUND_NAME_PM_WIN_BANNER});
    }

    private playPartyIntroSound(): void {
        OktoberfestSound.soundPlayer.playTrack({name: this._activePartyIntroSoundName});
    }

    private playPartyDrumSound(): void {
        OktoberfestSound.soundPlayer.playTrack({name: OktoberfestSound.SOUND_NAME_PM_MARCHING_DRUM});
    }

    private stopPartyDrumSound(): void {
        OktoberfestSound.soundPlayer.stopTrack(OktoberfestSound.SOUND_NAME_PM_MARCHING_DRUM);
        OktoberfestSound.soundPlayer.playTrack({name: OktoberfestSound.SOUND_NAME_PM_REEL_FIVE_LANDING});
    }
}
